import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useClientId } from '../hooks/useClientId';
import { useManagementSummary, useDownloadPdf, useShareLink } from '../hooks/useManagementSummary';
import { ApiErrorMessage } from '@/components/feedback/ApiErrorMessage';
import { formatEur } from '../format';

export function ManagementSummaryPage() {
  const { t } = useTranslation();
  const clientId = useClientId();
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'error'>('idle');

  const { data: summaryData, isLoading, isError, error, refetch } = useManagementSummary(clientId);
  const downloadPdf = useDownloadPdf(clientId);
  const shareLink = useShareLink(clientId);

  if (isError) {
    return <ApiErrorMessage error={error} onRetry={() => refetch()} />;
  }

  const summary = summaryData?.data;

  const handleCopyLink = async () => {
    try {
      const result = await shareLink.mutateAsync();
      if (navigator.clipboard) {
        await navigator.clipboard.writeText(result.data.url);
      } else {
        // Fallback for non-secure contexts
        const textarea = document.createElement('textarea');
        textarea.value = result.data.url;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopyStatus('copied');
      setTimeout(() => setCopyStatus('idle'), 3000);
    } catch {
      setCopyStatus('error');
      setTimeout(() => setCopyStatus('idle'), 3000);
    }
  };

  const copyLabel = copyStatus === 'copied' ? t('managementSummary.linkCopied')
    : copyStatus === 'error' ? t('managementSummary.copyError')
    : t('managementSummary.copyLink');

  return (
    <div className="space-y-5">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 style={{ fontSize: '1.25rem', fontWeight: 700, letterSpacing: '-0.02em', color: 'var(--foreground-heading)' }}>
          {t('managementSummary.title')}
        </h1>
        <div className="flex gap-2">
          <button
            onClick={() => downloadPdf.mutate()}
            disabled={downloadPdf.isPending || !summary}
            className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium text-white"
            style={{ backgroundColor: 'var(--primary)' }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>download</span>
            {downloadPdf.isPending ? t('common.loading') : t('managementSummary.downloadPdf')}
          </button>
          <button
            onClick={handleCopyLink}
            disabled={shareLink.isPending || !summary}
            className="flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-medium"
            style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
          >
            <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>link</span>
            {shareLink.isPending ? t('common.loading') : copyLabel}
          </button>
        </div>
      </div>

      {/* Download error feedback */}
      {downloadPdf.isError && (
        <p className="text-xs" style={{ color: 'var(--destructive)' }}>{t('managementSummary.downloadError')}</p>
      )}

      {/* A4 Preview */}
      {isLoading ? (
        <div className="mx-auto" style={{ maxWidth: '595px', aspectRatio: '1/1.414' }}>
          <div className="skeleton h-full w-full rounded-xl" />
        </div>
      ) : summary && (
        <div
          className="mx-auto rounded-xl overflow-hidden"
          style={{ maxWidth: '595px', backgroundColor: 'var(--background)', border: '1px solid var(--border-soft)', boxShadow: 'var(--card-shadow-md)' }}
        >
          <div className="px-8 py-6" style={{ backgroundColor: 'var(--primary)', color: 'white' }}>
            <p style={{ fontSize: '1.25rem', fontWeight: 600 }}>{t('managementSummary.reportTitle')}</p>
            <p className="mt-1 text-sm opacity-85">{summary.companyName} &middot; {summary.quarter}</p>
          </div>

          <div className="px-8 py-6 space-y-5">
            <p className="text-xs" style={{ color: 'var(--foreground-muted)' }}>
              {summary.altijdCorrect.totalProductsManaged} {t('managementSummary.productsManaged')} &middot; {summary.aanAlleEisenVoldaan.retailerProfilesActive} {t('managementSummary.retailers')}
            </p>

            <SummarySection icon="rocket_launch" iconColor="var(--success-dark)" title={t('value.speedToShelf')}>
              <p className="text-sm">{summary.snelInHetSchap.productsLaunchedThisQuarter} {t('managementSummary.productsLaunched')} &middot; {t('managementSummary.leadTime', { days: summary.snelInHetSchap.averageLeadTimeDays })}</p>
            </SummarySection>

            <SummarySection icon="verified_user" iconColor="var(--secondary)" title={t('value.compliance')}>
              <p className="text-sm">GS1: {summary.aanAlleEisenVoldaan.gs1FieldsAffected} {t('compliance.fieldsAffected')} &middot; PPWR: {t(`compliance.${summary.aanAlleEisenVoldaan.ppwrStatus === 'prepared' ? 'prepared' : 'actionNeeded'}`)}</p>
            </SummarySection>

            <SummarySection icon="shield" iconColor="var(--primary)" title={t('value.correctness')}>
              <p className="text-sm">{summary.altijdCorrect.totalProductsManaged} {t('managementSummary.productsManaged')} &middot; {summary.altijdCorrect.retailerQuestionsResolved} {t('managementSummary.questionsResolved')}</p>
            </SummarySection>

            <div className="rounded-lg px-4 py-3 text-center" style={{ backgroundColor: 'var(--primary-light)' }}>
              <p className="text-lg font-bold" style={{ color: 'var(--primary)' }}>
                {Math.round(summary.besparing.hoursSavedPerQuarter)} {t('dashboard.hourUnit')} &middot; {formatEur(summary.besparing.totalSavingEur)}
              </p>
              <p className="text-xs" style={{ color: 'var(--primary)' }}>{t('managementSummary.savingsThisQuarter')}</p>
            </div>

            {summary.benchmark.available && (
              <p className="text-xs text-center" style={{ color: 'var(--foreground-muted)' }}>
                {t('value.benchmarkTitle', { percentile: summary.benchmark.overallPercentile })}
              </p>
            )}

            <div className="border-t pt-3 text-center" style={{ borderColor: 'var(--border)' }}>
              <p className="text-xs font-semibold" style={{ color: 'var(--primary)' }}>{t('managementSummary.tagline')}</p>
              <p className="text-[0.625rem]" style={{ color: 'var(--foreground-muted)' }}>XXtract &middot; xxtract.nl</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function SummarySection({ icon, iconColor, title, children }: { icon: string; iconColor: string; title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="flex items-center gap-2 mb-1">
        <span className="material-symbols-outlined" style={{ fontSize: '16px', color: iconColor }}>{icon}</span>
        <p className="text-[0.8125rem] font-semibold" style={{ color: 'var(--foreground)' }}>{title}</p>
      </div>
      <div style={{ color: 'var(--foreground-muted)' }}>{children}</div>
    </div>
  );
}
